// Product design system — typed facade over the vendored compiled bundle.
//
// v1 keeps exact visual fidelity by re-exporting the design system's compiled components
// (which exist only as a bundle, not as TSX source) from the global namespace the bundle
// attaches. The components can later be reimplemented as first-class TSX behind this same
// module without touching any screen import.
//
// Two things happen on the way through:
//   - components whose values are load-bearing declare them (see the unions below), so a variant
//     or tone the bundle has no styling for is a compile error rather than an unstyled control;
//   - components with built-in German accessible names are wrapped in ./localised so those names
//     follow the operator's language.
import { cmp, type DSComponent, type DSProps, NS } from "./bundle";
import * as L from "./localised";

export type { DSProps } from "./bundle";

// ---- value vocabularies ------------------------------------------------------------------
//
// Each union is exactly what the bundle and Ribbon R presentation adapter have styling for. They are not decorative: a value
// outside them renders a control with no variant styling at all — a "danger" button that looks
// like unstyled text, an alert with no tone — and nothing fails, so it survives review and ships.
// #890 found two of those live in the Cockpit.

/** Quiet destructive entry and filled destructive confirmation are distinct roles. */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger-quiet" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

/** Inline alert tone. `system` is the navy, high-authority fail-closed tone. */
export type BannerTone = "info" | "success" | "warning" | "danger" | "system";

/** Status vocabulary tone — presentation tones, including processing separately from attention. */
export type StatusTone = "neutral" | "info" | "progress" | "warning" | "danger" | "success";
export type StatusPillVariant = "soft" | "solid" | "outline";

/** Dialog tone. */
export type DialogTone = "navy" | "danger" | "warning";

export type ButtonProps = DSProps & { variant?: ButtonVariant; size?: ButtonSize };
export type BannerProps = DSProps & { tone?: BannerTone };
export type StatusPillProps = DSProps & {
  tone?: StatusTone;
  variant?: StatusPillVariant;
  size?: "sm" | "md";
};
export type DialogProps = DSProps & { tone?: DialogTone; keepMounted?: boolean };

// ---- buttons ----
export { Button } from "./button";
export const SendButton: DSComponent = cmp("SendButton");

// ---- containers ----
export const Card: DSComponent = cmp("Card");
export const CollapsibleSection: DSComponent = cmp("CollapsibleSection");
export const DiffView: DSComponent = cmp("DiffView");
export const SplitPanel: DSComponent = cmp("SplitPanel");
export const Tabs: DSComponent = cmp("Tabs");

// ---- data ----
export const DataTable = L.DataTable;
export const FilterBar: DSComponent = cmp("FilterBar");
export const Pagination: DSComponent = cmp("Pagination");

// ---- feedback / overlays ----
export const Banner: DSComponent<BannerProps> = L.Banner;
export const Dialog: DSComponent<DialogProps> = cmp<DialogProps>("Dialog");
export const ConfirmDialog: DSComponent = cmp("ConfirmDialog");
export const Tooltip: DSComponent = cmp("Tooltip");

// ---- forms ----
export const Checkbox: DSComponent = cmp("Checkbox");
export const FormField = L.FormField;
export const Input: DSComponent = cmp("Input");
export const Radio: DSComponent = cmp("Radio");
export const Select: DSComponent = cmp("Select");
export const Switch: DSComponent = L.Switch;
export const Textarea: DSComponent = cmp("Textarea");

// ---- navigation ----
export const AppShell: DSComponent = cmp("AppShell");
export const Avatar: DSComponent = cmp("Avatar");
export const Breadcrumb = L.Breadcrumb;
export const NavItem: DSComponent = cmp("NavItem");
export const RoleBadge: DSComponent = cmp("RoleBadge");
export const SearchField: DSComponent = cmp("SearchField");

// ---- status / provenance ----
export const ConfidenceIndicator: DSComponent = cmp("ConfidenceIndicator");
export const EvidenceCallout: DSComponent = cmp("EvidenceCallout");
export const StatusPill: DSComponent<StatusPillProps> = cmp<StatusPillProps>("StatusPill");
export const Tag: DSComponent = cmp("Tag");
export const VersionStamp: DSComponent = cmp("VersionStamp");

// ---- timeline ----
export const TimelineItem: DSComponent = cmp("TimelineItem");

// ---- data maps exported by the bundle (not components) ----
export const SYSTEM_BANNERS = NS.SYSTEM_BANNERS as Record<string, { title: string; message: string }>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const REQUEST_LIFECYCLE = NS.REQUEST_LIFECYCLE as Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SEND_STATE = NS.SEND_STATE as Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FACT_STATUS = NS.FACT_STATUS as Record<string, any>;
